import { createSlice, Draft, Slice } from "@reduxjs/toolkit";
import { PAYMENT_SLICE_NAME } from "../shared/constants/store.constant.ts";
import { Align } from "../shared/enums/align.enum.ts";
import { RootState } from "./index.ts";
import { PayoutStateType } from "../shared/types/store/payout-state.type.ts";
import { PayoutType } from "../shared/types/store/payment-state.type.ts";
import { OperationSystemStatus } from "../shared/enums/operation-system-status.enum.ts";

const initialState: PayoutStateType = {
  headers: [
    { name: "Date", value: "createdAt", align: Align.LEFT },
    { name: "Type", value: "walletType", align: Align.LEFT },
    { name: "From", value: "paymentAddress", align: Align.LEFT },
    { name: "To", value: "payoutAddress", align: Align.LEFT },
    { name: "Amount", value: "amount", align: Align.LEFT },
    { name: "Description", value: "description", align: Align.LEFT },
    { name: "Status", value: "status", align: Align.CENTER },
    { name: "Action", value: "action", align: Align.CENTER },
  ],
  payouts: [],
  payout: null,
  payoutId: null,
  acceptModal: {
    payoutId: null,
  },
  rejectModal: {
    payoutId: null,
  },
  completeModal: {
    payoutId: null,
  },
  detailModal: {
    payoutId: null,
  },
  cancelModal: {
    payoutId: null,
  },
  sweepModal: {
    payoutId: null,
  },
  pagination: {
    page: 1,
    perPage: 10,
    total: 0,
  },
};

const slice: Slice<PayoutStateType> = createSlice({
  name: PAYMENT_SLICE_NAME,
  initialState,
  reducers: {
    setPayouts: (state: Draft<PayoutStateType>, { payload }): void => {
      state.payouts = payload;
    },
    setPayout: (state: Draft<PayoutStateType>, { payload }): void => {
      state.payout = payload;
    },
    setStatus: (state: Draft<PayoutStateType>, { payload }) => {
      const index: number = state.payouts.findIndex(
        (p: any) => p.id === payload.id,
      );

      if (index !== -1) {
        state.payouts[index].status = payload.status;
      }

      if (state.payout && state.payout.id === payload.id) {
        state.payout.status = payload.status;
      }
    },
    setSysStatus: (state: Draft<PayoutStateType>, { payload }) => {
      // Only a sweep reaches COMPLETED from IN_SWEEP, and it leaves nothing worth another pass.
      const apply = (payout: Draft<PayoutType>): void => {
        if (
          payout.sysStatus === OperationSystemStatus.IN_SWEEP &&
          payload.sysStatus === OperationSystemStatus.COMPLETED
        ) {
          payout.address = { balances: [] };
        }

        payout.sysStatus = payload.sysStatus;
      };

      const index: number = state.payouts.findIndex(
        (p: PayoutType) => p.id === payload.id,
      );

      if (index !== -1) {
        apply(state.payouts[index]);
      }

      if (state.payout && state.payout.id === payload.id) {
        apply(state.payout);
      }
    },
    updatePayoutAddressStatus: (state: Draft<PayoutStateType>, { payload }) => {
      state.payouts.forEach((p, index) => {
        if (payload.addressId === p.payoutAddress?.id) {
          state.payouts[index].payoutAddress.status = payload.status;
        }
      });
    },
    addPayout: (state: Draft<PayoutStateType>, { payload }) => {
      const index: number = state.payouts.findIndex(
        (p: any) => p.id === payload.id,
      );

      if (index === -1) {
        state.payouts.unshift(payload);
      }
    },
    setPayoutPagination: (state: Draft<PayoutStateType>, { payload }): void => {
      state.pagination = payload;
    },
    setPayoutError: (state: Draft<PayoutStateType>, { payload }): void => {
      const index: number = state.payouts.findIndex(
        (p: any) => p.id === payload.operationId,
      );

      if (index !== -1) {
        state.payouts[index].error = payload.error;
      }
    },
    openAcceptPayoutModal: (
      state: Draft<PayoutStateType>,
      { payload },
    ): void => {
      state.acceptModal.payoutId = payload;
    },
    closeAcceptPayoutModal: (state: Draft<PayoutStateType>): void => {
      state.acceptModal.payoutId = null;
    },
    openRejectPayoutModal: (
      state: Draft<PayoutStateType>,
      { payload },
    ): void => {
      state.rejectModal.payoutId = payload;
    },
    closeRejectPayoutModal: (state: Draft<PayoutStateType>): void => {
      state.rejectModal.payoutId = null;
    },
    openDetailPayoutModal: (
      state: Draft<PayoutStateType>,
      { payload },
    ): void => {
      state.detailModal.payoutId = payload;
    },
    closeDetailPayoutModal: (state: Draft<PayoutStateType>): void => {
      state.detailModal.payoutId = null;
    },
    openCompletePayoutModal: (
      state: Draft<PayoutStateType>,
      { payload },
    ): void => {
      state.completeModal.payoutId = payload;
    },
    closeCompletePayoutModal: (state: Draft<PayoutStateType>): void => {
      state.completeModal.payoutId = null;
    },
    openCancelPayoutModal: (
      state: Draft<PayoutStateType>,
      { payload },
    ): void => {
      state.cancelModal.payoutId = payload;
    },
    closeCancelPayoutModal: (state: Draft<PayoutStateType>): void => {
      state.cancelModal.payoutId = null;
    },
    openSweepPayoutModal: (
      state: Draft<PayoutStateType>,
      { payload },
    ): void => {
      state.sweepModal.payoutId = payload;
    },
    closeSweepPayoutModal: (state: Draft<PayoutStateType>): void => {
      state.sweepModal.payoutId = null;
    },
  },
});

export const payoutSelector = (state: RootState) => state.payout;
export const {
  setPayouts,
  setPayout,
  setPayoutPagination,
  setStatus,
  setSysStatus,
  setPayoutError,
  updatePayoutAddressStatus,
  openAcceptPayoutModal,
  closeAcceptPayoutModal,
  openRejectPayoutModal,
  closeRejectPayoutModal,
  addPayout,
  openCompletePayoutModal,
  closeCompletePayoutModal,
  openDetailPayoutModal,
  closeDetailPayoutModal,
  openCancelPayoutModal,
  closeCancelPayoutModal,
  openSweepPayoutModal,
  closeSweepPayoutModal,
} = slice.actions;
export default slice.reducer;
